import { useEffect, useMemo, useState } from "react";
import NetworkService from "../../../NetworkService";
import {
  buildPriceEstimateDependencyKey,
  buildRideOrderEstimatePayload,
  canEstimateRidePrice,
  getRideOrderApiErrorMessage,
} from "../rideOrderPayload";
import { DEFAULT_ORDER_FORM_LOCALE } from "../orderFormLocale";
import { OrderFormLocale, OrderFormState, RideType } from "../types";

export type PriceEstimateState = {
  status: "idle" | "loading" | "ready" | "error";
  price: number | null;
  message: string | null;
};

const INITIAL_STATE: PriceEstimateState = {
  status: "idle",
  price: null,
  message: null,
};

type UseOrderPriceEstimateArgs = {
  form: OrderFormState;
  rideType: RideType;
  companyAdminId?: number;
  locale?: OrderFormLocale;
};

export function useOrderPriceEstimate({
  form,
  rideType,
  companyAdminId,
  locale,
}: UseOrderPriceEstimateArgs): PriceEstimateState {
  const priceLabels = (locale ?? DEFAULT_ORDER_FORM_LOCALE).price ?? DEFAULT_ORDER_FORM_LOCALE.price;
  const [estimate, setEstimate] = useState<PriceEstimateState>(INITIAL_STATE);

  const estimateKey = useMemo(
    () => buildPriceEstimateDependencyKey(form, rideType),
    [
      rideType,
      form.pickup,
      form.dropoff,
      form.pickupCoords,
      form.dropoffCoords,
      form.waypoints,
      form.durationHours,
      form.transferMode,
      form.carClass,
      form.vehicleId,
    ]
  );

  useEffect(() => {
    if (!companyAdminId || !canEstimateRidePrice(form, rideType)) {
      setEstimate(INITIAL_STATE);
      return undefined;
    }

    const payload = buildRideOrderEstimatePayload(form, rideType);
    if (!payload) {
      setEstimate(INITIAL_STATE);
      return undefined;
    }

    let cancelled = false;
    setEstimate({ status: "loading", price: null, message: null });

    const timer = window.setTimeout(() => {
      NetworkService.estimateCompanyRidePrice(payload, companyAdminId)
        .then((response: { price?: number }) => {
          if (cancelled) return;

          if (typeof response?.price !== "number") {
            setEstimate({
              status: "error",
              price: null,
              message: priceLabels?.error ?? "Unable to calculate price",
            });
            return;
          }

          setEstimate({
            status: "ready",
            price: response.price,
            message: null,
          });
        })
        .catch((error) => {
          if (cancelled) return;
          setEstimate({
            status: "error",
            price: null,
            message: getRideOrderApiErrorMessage(error),
          });
        });
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [companyAdminId, estimateKey, priceLabels?.error, rideType]);

  return estimate;
}
